import type { ShotInput, SportsGameSettings, DistanceBand } from '../../sports.types';
import type {
  ISportDefinition,
  SportAssets,
  SportPhysicsParams,
  StartingPosition,
  ShotEvaluation,
} from '../sport-definition.interface';

// ─── Physics constants ─────────────────────────────────────────────────────────

const PHYSICS: SportPhysicsParams = {
  ballRadius: 0.05,

  // power 1.0 at optimal elevation → ~1.53 scene units forward
  // (power × 2.0 × cos(40°) = power × 1.53)
  // Good:      min power ~0.65   (≈ 0.5–1.0 comfortable range)
  // Great:     min power ~1.63   (≈ 1.5–2.0 comfortable range)
  // Excellent: min power ~2.61   (≈ 2.5–3.0 comfortable range)
  launchSpeedScale: 2.0,

  // Ball rises to ~45% of forward distance at arc peak — looks like a real drive
  arcHeightFactor: 1.2,   // visually dramatic drive arc

  assistCorrectionStrength: 0.5,

  // Golf driving is distance-scored; goalToleranceRadius is not used for hit-testing,
  // but a value is required by the interface.
  goalToleranceRadius: 0,

  maxLandingDistance: 7.0,  // generous upper bound; no OOB penalty
  optimalElevationDeg: 40,
};

// ─── Default distance bands ────────────────────────────────────────────────────

/**
 * Default distance bands for golf driving.
 * Bands are sorted ascending. The renderer picks the highest band whose
 * minDistance ≤ actual landing distance.
 *
 * Operators can override these via SportsGameSettings.distanceBands.
 */
const DEFAULT_DISTANCE_BANDS: DistanceBand[] = [
  { minDistance: 0,   points: 0,  label: 'Whiff!'      },
  { minDistance: 1.0, points: 5,  label: 'Good Drive'  },
  { minDistance: 2.5, points: 10, label: 'Great Drive' },
  { minDistance: 4.0, points: 20, label: 'Excellent!'  },
];

// ─── Starting position ─────────────────────────────────────────────────────────

// Golf driving uses a single fixed tee. All attempts launch from the same spot.
// The challenge is finding the right power + elevation — not position variety.
const STARTING_POSITIONS: StartingPosition[] = [
  { lateralOffset: 0, distance: 0 },
];

// ─── Assets ────────────────────────────────────────────────────────────────────

const ASSETS: SportAssets = {
  // Phase 1: no models — scene renders a white procedural sphere
  // Phase 3+: ballModelPath: 'assets/sports/golf/golf-ball.glb'
  ballColor: '#ffffff',
  // Phase 3+: goalModelPath, groundTexturePath
};

// ─── Sport definition ──────────────────────────────────────────────────────────

export class GolfDrivingSportDefinition implements ISportDefinition {
  readonly id = 'golf-driving' as const;
  readonly displayName = 'Golf Drive';
  readonly scoringModel = 'distance-bands' as const;
  readonly assets = ASSETS;
  readonly physicsParams = PHYSICS;
  readonly startingPositions = STARTING_POSITIONS;
  readonly defaultDistanceBands = DEFAULT_DISTANCE_BANDS;
  readonly maxAttempts = 10; // no practical upper bound for a driving game

  readonly arCameraHint = { pitch: -30, yaw: 0 };

  // Golf driving: marker on the ground ahead of the player (virtual tee location).
  // Scene lies flat on the horizontal plane.
  readonly arSceneRotation: [number, number, number] = [-Math.PI / 2, 0, 0];

  evaluateShot(
    input: ShotInput,
    assist: number,
    settings: SportsGameSettings
  ): ShotEvaluation {
    const { launchSpeedScale, optimalElevationDeg = 40 } = this.physicsParams;

    // ── Step 1: Over-swing check ──────────────────────────────────────────────
    // Swiping too fast causes a mis-hit (topping / duffing the ball).
    if (input.overSwing) {
      console.log('[GolfDriving] WHIFF (over-swing)', { power: input.power.toFixed(3) });
      return {
        scored: false, pointsEarned: 0, distance: 0,
        finalPosition: { x: 0, y: 0, z: 0 }, isWhiff: true,
      };
    }

    // ── Step 2: Map elevation component to launch angle ───────────────────────
    // direction.y [0..1] → angle in degrees [0 .. optimalElevationDeg × 2]
    // At direction.y = 0.5 the player hits exactly the optimal elevation.
    const launchAngleDeg = input.direction.y * optimalElevationDeg * 2;
    const launchAngleRad = (launchAngleDeg * Math.PI) / 180;

    const cosAngle = Math.cos(launchAngleRad);
    const sinAngle = Math.sin(launchAngleRad);

    // ── Step 3: Base horizontal distance (before assist / noise) ────────────────
    const baseDistance = input.power * launchSpeedScale * cosAngle;

    // ── Step 4: Steep-angle whiff guard ─────────────────────────────────────────────
    // Safeguard for external ShotInput that might supply direction.y > 0.9.
    const whiffAngleThreshold = 72;
    if (launchAngleDeg > whiffAngleThreshold && input.power > 0.5) {
      console.log('[GolfDriving] WHIFF (steep angle)', {
        launchAngleDeg: launchAngleDeg.toFixed(1),
        power: input.power.toFixed(3),
        whiffAngleThreshold,
      });
      return {
        scored: false,
        pointsEarned: 0,
        distance: 0,
        finalPosition: { x: 0, y: 0, z: 0 },
        isWhiff: true,
      };
    }

    // ── Step 5: Add distance noise scaled by skill level ────────────────────────
    const noiseMagnitude = baseDistance * 0.1 * (1 - assist);
    const noise = (Math.random() * 2 - 1) * noiseMagnitude;
    const landingDistance = Math.max(0, baseDistance + noise);

    // ── Step 6: Determine distance band ─────────────────────────────────────────
    const bands = (settings.distanceBands?.length ? settings.distanceBands : this.defaultDistanceBands)!;
    const band = this.resolveBand(landingDistance, bands);

    // ── Step 7: Final position ─────────────────────────────────────────────────────────
    // Sidespin: direction.x (horizontal swipe component) curves the ball
    // left/right proportional to how far it travelled (like a draw or slice).
    // Scales independently of assist so sidespin is always a meaningful mechanic.
    const spinDrift = input.direction.x * landingDistance * 0.35;
    const lateralNoise = (Math.random() * 2 - 1) * 0.1 * (1 - assist);
    const finalX = spinDrift + lateralNoise;

    console.log('[GolfDriving] evaluateShot', {
      'input.power': input.power.toFixed(3),
      'input.direction.x': input.direction.x.toFixed(3),
      'input.direction.y': input.direction.y.toFixed(3),
      overSwing: input.overSwing ?? false,
      assist: assist.toFixed(2),
      launchAngleDeg: launchAngleDeg.toFixed(1),
      cosAngle: cosAngle.toFixed(3),
      launchSpeedScale,
      baseDistance: baseDistance.toFixed(3),
      noise: noise.toFixed(3),
      landingDistance: landingDistance.toFixed(3),
      band: band.label,
      points: band.points,
      spinDrift: spinDrift.toFixed(3),
      finalX: finalX.toFixed(3),
      whiffAngleThreshold,
    });

    return {
      scored: band.points > 0,
      pointsEarned: band.points,
      distance: landingDistance,
      zone: band.label,
      finalPosition: { x: finalX, y: 0, z: -landingDistance },
      isWhiff: false,
    };
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private resolveBand(distance: number, bands: DistanceBand[]): DistanceBand {
    // Walk bands in descending order — return the highest band the distance qualifies for.
    const sorted = [...bands].sort((a, b) => b.minDistance - a.minDistance);
    for (const band of sorted) {
      if (distance >= band.minDistance) return band;
    }
    // Below all thresholds — return the lowest (typically 0-point whiff band)
    return bands.reduce((min, b) => (b.minDistance < min.minDistance ? b : min));
  }
}
